import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-[color,background-color,border-color,box-shadow,transform] duration-150 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Sunflower with a darker edge underneath, as in FlightStay. Pressing flattens the edge
        // rather than moving the button, so a click near its border can't slip off it mid-press.
        default:
          'bg-primary text-primary-foreground shadow-[0_3px_0_hsl(var(--primary-edge))] hover:brightness-105 active:shadow-[0_1px_0_hsl(var(--primary-edge))] active:brightness-95',
        brand:
          'bg-primary text-primary-foreground shadow-[0_4px_0_hsl(var(--primary-edge))] hover:shadow-[0_4px_0_hsl(var(--primary-edge)),0_14px_30px_-12px_hsl(var(--brand-sun)/0.8)] active:shadow-[0_1px_0_hsl(var(--primary-edge))] active:brightness-95',
        // Navy ink, the FlightStay secondary action (inverts in dark mode)
        ink: 'bg-foreground text-background hover:bg-foreground/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
        outline: 'border border-input bg-card/60 hover:border-primary/40 hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-secondary',
        destructive: 'bg-destructive text-destructive-foreground hover:brightness-110',
        success: 'bg-success text-success-foreground hover:brightness-110',
        link: 'text-primary-ink underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-xl px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={asChild ? undefined : disabled || loading}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading && <Loader2 className="animate-spin" />}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
